import React from 'react';
import {
	Box,
	Center,
	CenterProps,
	Flex,
	keyframes,
	SpinnerProps,
	Text,
	VisuallyHidden,
} from '@chakra-ui/react';
import { S8MainLogo } from '../../assets';

const compactSweep = keyframes`
	0% {
		transform: translate3d(-110%, 0, 0) scaleX(0.72);
	}
	50% {
		transform: translate3d(95%, 0, 0) scaleX(1.08);
	}
	100% {
		transform: translate3d(300%, 0, 0) scaleX(0.72);
	}
`;

const brandTrackSweep = keyframes`
	0% {
		transform: translate3d(-100%, 0, 0);
	}
	100% {
		transform: translate3d(300%, 0, 0);
	}
`;

const brandFadeIn = keyframes`
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
`;

const COMPACT_LOADER_SIZES = {
	sm: { height: '2px', segmentWidth: '7px', width: '20px' },
	md: { height: '3px', segmentWidth: '9px', width: '26px' },
	lg: { height: '3px', segmentWidth: '11px', width: '32px' },
	xl: { height: '4px', segmentWidth: '14px', width: '40px' },
} as const;

type LoaderVariant = 'brand' | 'compact';

/** Brand loader on the light app background, or on the navy of the sign-in pages (session check). */
type LoaderTone = 'light' | 'navy';

const NAVY_BACKGROUND =
	'linear-gradient(160deg, #24346E 0%, #1C2959 45%, #0F1B49 100%)';

interface ILoaderProps {
	variant?: LoaderVariant;
	tone?: LoaderTone;
	label?: string;
	spinnerSize?: string;
	centerHeight?: string;
	centerProps?: CenterProps;
	spinnerProps?: SpinnerProps;
}

const CompactLoader = ({
	spinnerSize,
	spinnerProps,
}: Pick<ILoaderProps, 'spinnerSize' | 'spinnerProps'>) => {
	const size =
		COMPACT_LOADER_SIZES[
			spinnerSize as keyof typeof COMPACT_LOADER_SIZES
		] ?? COMPACT_LOADER_SIZES.xl;
	const {
		color = 'success',
		emptyColor = 'lightBlue.100',
		speed = '1.1s',
		thickness: _thickness,
		...trackProps
	} = spinnerProps ?? {};

	return (
		<Box
			position="relative"
			width={size.width}
			height={size.height}
			borderRadius="full"
			bg={emptyColor}
			overflow="hidden"
			{...trackProps}>
			<Box
				position="absolute"
				insetY={0}
				left={0}
				width={size.segmentWidth}
				borderRadius="full"
				bg={color}
				transformOrigin="center"
				animation={`${compactSweep} ${speed} cubic-bezier(0.65, 0, 0.35, 1) infinite`}
				sx={{
					'@media (prefers-reduced-motion: reduce)': {
						animation: 'none',
						left: '50%',
						transform: 'translateX(-50%)',
					},
				}}
			/>
		</Box>
	);
};

// Logo, a track with a sliding green segment, and the label. It fades in after a moment, so quick loads never flash it.
const BrandLoader = ({ label, tone }: Pick<ILoaderProps, 'label' | 'tone'>) => {
	const onNavy = tone === 'navy';

	return (
		<Flex
			direction="column"
			align="center"
			animation={`${brandFadeIn} 300ms ease-out 300ms both`}
			sx={{
				'@media (prefers-reduced-motion: reduce)': {
					animation: 'none',
				},
			}}>
			<Box aria-hidden={true}>
				<S8MainLogo
					width={174}
					height={36}
					wordmarkColor={onNavy ? 'white' : '#1C2959'}
				/>
			</Box>
			<Box
				position="relative"
				mt="32px"
				width="160px"
				height="4px"
				borderRadius="full"
				bg={onNavy ? 'rgba(255, 255, 255, 0.14)' : 'lightBlue.100'}
				overflow="hidden"
				aria-hidden={true}>
				<Box
					position="absolute"
					insetY={0}
					left={0}
					width="33%"
					borderRadius="full"
					bg="success"
					animation={`${brandTrackSweep} 1.4s cubic-bezier(0.65, 0, 0.35, 1) infinite`}
					sx={{
						'@media (prefers-reduced-motion: reduce)': {
							animation: 'none',
							width: '100%',
							opacity: 0.45,
						},
					}}
				/>
			</Box>
			{onNavy ? (
				<VisuallyHidden>{label}</VisuallyHidden>
			) : (
				<Text
					mt="18px"
					color="blue.50"
					fontSize="14px"
					fontWeight={600}
					lineHeight="20px"
					letterSpacing="0.2px">
					{label}
				</Text>
			)}
		</Flex>
	);
};

export const Loader: React.FC<ILoaderProps> = ({
	variant = 'compact',
	tone = 'light',
	label = 'Loading',
	spinnerSize = 'xl',
	centerHeight,
	centerProps,
	spinnerProps,
}) => (
	<Center
		h={centerHeight}
		bg={variant === 'brand' && tone === 'light' ? 'mainBg' : undefined}
		bgImage={
			variant === 'brand' && tone === 'navy' ? NAVY_BACKGROUND : undefined
		}
		role="status"
		aria-busy="true"
		aria-live="polite"
		{...centerProps}>
		{variant === 'brand' ? (
			<BrandLoader label={label} tone={tone} />
		) : (
			<>
				<CompactLoader
					spinnerSize={spinnerSize}
					spinnerProps={spinnerProps}
				/>
				<VisuallyHidden>{label}</VisuallyHidden>
			</>
		)}
	</Center>
);
